import { DOCUMENT } from '@angular/common';
import { Injectable, inject } from '@angular/core';

import { GameType } from '@bpos/v1/sports-promo';
import { CRMTokenImages, CrmConfig, Sitecore } from '@frontend/sports/common/client-config-data-access';
import { SportConstant } from '@frontend/sports/common/core/data-access/constants';
import { DateProviderService } from '@frontend/sports/common/core/utils/date';
import { ScrollContainerService } from '@frontend/sports/common/core/utils/scroll-container';
import { interpolateDoubleBracesString } from '@frontend/sports/common/core/utils/string';
import { NOT_APPLICABLE, TrackingService, trackingConstants } from '@frontend/sports/tracking/feature';
import { Cards } from '@frontend/sports/types/components/sitecore';
import { UserService } from '@frontend/sports/user/feature';
import { DeviceService, IntlService, LocalStoreService, NavigationService, OnAppInit } from '@frontend/vanilla/core';
import { RedirectHelperService } from 'packages/sports/web/app/src/navigation-core/redirect-helper.service';
import { SportUrlParam, UrlHelperService } from 'packages/sports/web/app/src/navigation-core/url-helper.service';
import { PromoHubUrlTokenType, RewardTokenImages, RewardTokenType } from 'packages/sports/web/app/src/tokens-base/token-base.models';
import { BehaviorSubject, Observable, Subject, take } from 'rxjs';

import { IRewardToken, RewardBannerTrackingConstants } from '../reward-tokens.model';
import { parseMarketType } from './reward-tokens.utils';

export interface TokenCategories {
    sportsCatergory?: string;
    liveCategory?: string;
    competitionsCategory?: string;
    meetingsCategory?: string;
    fixturesCategory?: string;
    marketsCategory?: string;
    marketTemplatesCategory?: string;
    marketParametersCategory?: string;
    optionsCategory?: string;
    prematchCategory?: string;
}
@Injectable({
    providedIn: 'root',
})
export class RewardTokensBannerService implements OnAppInit {
    private readonly _doc = inject(DOCUMENT);
    private readonly betRewardBannerDismissKey = 'IsBetRewardBannerDismissed';
    private readonly MINUTE = 60 * 1000;
    private readonly HOUR = 60 * this.MINUTE;
    private readonly DAY = 24 * this.HOUR;
    private _recentlyClickedToken$ = new BehaviorSubject<string | null>(null);
    readonly recentlyClickedToken$ = this._recentlyClickedToken$.asObservable();
    private _isBannerExpaned = new Subject<boolean>();
    private isBannerExpanded = false;
    isRewardDrawerEnabled = this.tokensConfig.tokens.isRewardDrawerEnabled;

    constructor(
        public sitecore: Sitecore,
        private userService: UserService,
        private localStoreService: LocalStoreService,
        private trackingService: TrackingService,
        private redirectHelperService: RedirectHelperService,
        private scrollContainer: ScrollContainerService,
        private navigationService: NavigationService,
        private urlHelperService: UrlHelperService,
        private deviceService: DeviceService,
        private crmTokenimage: CRMTokenImages,
        private intlService: IntlService,
        private tokensConfig: CrmConfig,
    ) {}

    onAppInit(): void {
        if (this.userService.isAuthenticated || !this.isBetRewardBannerDismissed()) {
            return;
        }

        this.userService.onUserLogin$.pipe(take(1)).subscribe(() => {
            this.setBetRewardBannerStorageKey(false);
        });
    }

    setBetRewardBannerStorageKey(isBetRewardBannerDismissed: boolean): void {
        this.localStoreService.set(this.betRewardBannerDismissKey, isBetRewardBannerDismissed);
    }

    isBetRewardBannerDismissed(): boolean {
        return this.localStoreService.get(this.betRewardBannerDismissKey) ?? false;
    }

    trackBetRewardBanner(
        action: RewardBannerTrackingConstants,
        baseTracking: { [key: string]: string },
        eventDetail: string,
        positionEvent?: string,
        url?: string,
        page?: string,
    ): void {
        this.trackingService.track(
            action === RewardBannerTrackingConstants.Load ? trackingConstants.EVENT_CONTENT_VIEW : trackingConstants.EVENT_TRACKING,
            {
                [trackingConstants.COMPONENT_CATEGORY_EVENT]: RewardBannerTrackingConstants.PromoHub,
                [trackingConstants.COMPONENT_LABEL_EVENT]: RewardBannerTrackingConstants.BetRewardsBanner,
                [trackingConstants.COMPONENT_ACTION_EVENT]: action,
                [trackingConstants.COMPONENT_POSITION_EVENT]: positionEvent ?? NOT_APPLICABLE,
                [trackingConstants.COMPONENT_LOCATION_EVENT]:
                    action === RewardBannerTrackingConstants.Click ? RewardBannerTrackingConstants.BetRewardsBanner : (page ?? NOT_APPLICABLE),
                [trackingConstants.COMPONENT_EVENT_DETAILS]: eventDetail,
                [trackingConstants.COMPONENT_URL_CLICKED]: url ?? NOT_APPLICABLE,
                ...baseTracking,
            },
        );
    }

    betNowRedirect(token: IRewardToken): void {
        this.setRecentlyClicked(null);

        if (this.isRewardDrawerEnabled && token.url) {
            this.redirectHelperService.goToPage(token.url);
        } else {
            const fixturesCount = token.filter?.fixtures?.length;
            if (fixturesCount === 1) {
                // For racing fixture filter cretira there is no meeting details, so navigate to racing sport lobby page
                if ([SportConstant.Horses, SportConstant.Greyhounds].includes(token.filter.sports[0].id)) {
                    this.redirectToSport(token);
                } else {
                    this.redirectHelperService.goToEvent(token.filter.fixtures[0]);
                }
            } else if (token.filter?.competitions?.length === 1) {
                const competitionUrl = this.urlHelperService.getSportUrl(
                    token.filter.sports[0],
                    SportUrlParam.Betting,
                    { id: '', name: '' },
                    { id: token.filter.competitions[0].id, name: '' },
                );
                this.redirectHelperService.goToPage(competitionUrl);
            } else if (token.filter?.meetings?.length === 1) {
                const param = {
                    id: token.filter.meetings[0].id,
                    name: '',
                };
                this.redirectHelperService.goToRacing(token.filter.sports[0].id, param);
            } else if (token.filter?.gameType === GameType.InPlay) {
                this.redirectToLiveSport(token);
            } else {
                this.redirectToSport(token);
            }
        }
    }

    private homeRedirection(): void {
        const currentUrl = this.navigationService.location.path();
        const scrollHeight = this._doc.querySelector('.cards-module')?.scrollHeight;
        if (currentUrl === this.urlHelperService.getHomeUrl() && this.deviceService.isMobile && !this.isRewardDrawerEnabled) {
            this.scrollContainer.scrollTo(0, 0);
            this.scrollContainer.scrollToDelta(0, scrollHeight);
        } else if (this.deviceService.isMobile && this.isRewardDrawerEnabled) {
            this.setRecentlyClicked(null);
            this.toggleBannerExpansion();
            if (currentUrl === this.urlHelperService.getHomeUrl()) {
                this.scrollOnBetNow();
            }
        } else {
            this.redirectHelperService.goHome(undefined, true);
        }
    }

    private redirectToLiveSport(token: IRewardToken): void {
        const liveSportsCount = token.filter.sports?.length;
        if (liveSportsCount === 1) {
            this.redirectHelperService.goToLiveSport({ sport: token.filter.sports[0] });
        } else {
            this.redirectHelperService.goToLive();
        }
    }

    private redirectToSport(token: IRewardToken): void {
        const sportsCount = token.filter?.sports?.length;
        const currentUrl = this.navigationService.location.path();
        const scrollHeight = this._doc.querySelector('.cards-module')?.scrollHeight;

        if (sportsCount === 1) {
            if (currentUrl === this.urlHelperService.getSportUrl(token.filter.sports[0]) && this.deviceService.isMobile) {
                this.scrollContainer.scrollTo(0, 0);
                this.scrollContainer.scrollToDelta(0, scrollHeight);
            } else {
                this.redirectHelperService.goToSport(token.filter.sports[0]);
            }
        } else {
            this.setRecentlyClicked(token.userTokenId);
            this.homeRedirection();
        }
    }

    setTokenInfoUrl(promoHubUrlTokenType: string, userTokenId: string): void {
        const promoBaseUrl = this.sitecore.crmTokens.links.promotiondetails.url;
        const promoBaseUrlSeparator = promoBaseUrl?.endsWith('/') ? '' : '/';
        const tokenInfoUrl = `${promoBaseUrl}${promoBaseUrlSeparator}${promoHubUrlTokenType}/${userTokenId}`;
        this.redirectHelperService.goToPage(tokenInfoUrl);
    }

    getIconUrl(isRewardBannerView?: boolean): { [id: string]: string } {
        const isDarkMode = this.tokensConfig.tokens.enableDarkModeForRewardDrawer && isRewardBannerView;
        const rewardsIcons = {} as { [id: string]: string };

        rewardsIcons[RewardTokenType.FreeBet] = this.crmTokenimage.crmTokenImages.find(
            (x) => x.name === (isDarkMode ? RewardTokenImages.FreeBetDark : RewardTokenImages.FreeBet),
        )?.src!;
        rewardsIcons[RewardTokenType.OddsBoost] = this.crmTokenimage.crmTokenImages.find(
            (x) => x.name === (isDarkMode ? RewardTokenImages.OddsBoostDark : RewardTokenImages.OddsBoost),
        )?.src!;
        rewardsIcons[RewardTokenType.RiskFreeBet] = this.crmTokenimage.crmTokenImages.find(
            (x) => x.name === (isDarkMode ? RewardTokenImages.RiskFreeBetDark : RewardTokenImages.RiskFreeBet),
        )?.src!;
        rewardsIcons[RewardTokenType.BetAndGet] = this.crmTokenimage.crmTokenImages.find(
            (x) => x.name === (isDarkMode ? RewardTokenImages.BetAndGetDark : RewardTokenImages.BetAndGet),
        )?.src!;

        return rewardsIcons;
    }

    getTokenUrlDetails(token: IRewardToken): { tokenInfoUrl: string; tokenInfoLinkText: string; promoHubUrlTokenType: string } {
        const promoCtaLinks = this.sitecore.crmTokens.ctaLinks.promoCtaLinks;
        switch (token.rewardTokenType) {
            case RewardTokenType.FreeBet:
                return {
                    tokenInfoUrl: this.getTokenUrl(promoCtaLinks.FreeBet, this.sitecore.crmTokens.cards.FreeBetsDetailsUrl, token),
                    tokenInfoLinkText: this.sitecore.crmTokens.banner.FreebetDetailsLinkTitle,
                    promoHubUrlTokenType: PromoHubUrlTokenType.FreeBet,
                };
            case RewardTokenType.RiskFreeBet:
                return {
                    tokenInfoUrl: this.getTokenUrl(promoCtaLinks.RiskFreeBet, this.sitecore.crmTokens.cards.RiskFreeBetDetailsUrl, token),
                    tokenInfoLinkText: this.sitecore.crmTokens.banner.BackupBetDetailsLinkTitle,
                    promoHubUrlTokenType: PromoHubUrlTokenType.FreeBet,
                };
            case RewardTokenType.OddsBoost:
                return {
                    tokenInfoUrl: this.getTokenUrl(promoCtaLinks.OddsBoost, this.sitecore.crmTokens.cards.OddBoostDetailsUrl, token),
                    tokenInfoLinkText: this.sitecore.crmTokens.banner.OddsBoostDetailsLinkTitle,
                    promoHubUrlTokenType: PromoHubUrlTokenType.FreeBet,
                };
            case RewardTokenType.BetAndGet:
                return {
                    tokenInfoUrl: this.getTokenUrl(promoCtaLinks.BetAndGet, this.sitecore.crmTokens.cards.BetAndGetDetailsUrl, token),
                    tokenInfoLinkText: this.sitecore.crmTokens.banner.BetAndGetDetailsLinkTitle,
                    promoHubUrlTokenType: PromoHubUrlTokenType.FreeBet,
                };
            default:
                return { tokenInfoUrl: '', tokenInfoLinkText: '', promoHubUrlTokenType: PromoHubUrlTokenType.FreeBet };
        }
    }

    private getTokenUrl(promoHubUrlTokenType: string, tokenUrl: string, token: IRewardToken): string {
        const promoBaseUrl = this.sitecore.crmTokens.links.promotiondetails.url;
        const promoBaseUrlSeparator = promoBaseUrl?.endsWith('/') ? '' : '/';
        if (this.isRewardDrawerEnabled) {
            return interpolateDoubleBracesString(tokenUrl, {
                promoBaseUrl: promoBaseUrl ?? '',
                promoBaseUrlSeparator,
                userTokenId: token.userTokenId,
            });
        } else {
            return `${promoBaseUrl}${promoBaseUrlSeparator}${promoHubUrlTokenType}/${token.userTokenId}`;
        }
    }

    getTokenTitle(token: IRewardToken): string {
        switch (token.rewardTokenType) {
            case RewardTokenType.FreeBet:
                return token.customTitle ? token.customTitle : this.sitecore.crmTokens.messages.Freebet;
            case RewardTokenType.RiskFreeBet:
                return token.customTitle ? token.customTitle : this.sitecore.crmTokens.riskFree.card.DefaultTitle;
            case RewardTokenType.OddsBoost:
                if (token.tokenType === 'DEFAULT') {
                    return this.sitecore.crmTokens.oddsBoost.card.OddsBoostTypeRegular;
                } else if (token.tokenType === 'MEGA') {
                    return this.sitecore.crmTokens.oddsBoost.card.OddsBoostTypeMega;
                } else if (token.tokenType === 'SUPER') {
                    return this.sitecore.crmTokens.oddsBoost.card.OddsBoostTypeSuper;
                } else {
                    if (token.tokenType === 'CUSTOM' && token.customTitle) {
                        return token.customTitle;
                    }

                    return this.sitecore.crmTokens.messages.OddsBoost;
                }
            case RewardTokenType.BetAndGet:
                return token.customTitle ? token.customTitle : this.sitecore.crmTokens.messages.BetAndGet;
        }

        return '';
    }

    getTokenCategories(token: IRewardToken): TokenCategories {
        const tokenCards: Cards = this.sitecore.crmTokens.cards;
        const result: TokenCategories = {};
        const separator = tokenCards.TokenCategoryDelimiter;
        const isLiveSport = token.filter?.gameType === GameType.InPlay;

        const sportsCount = token.filter?.sports?.length;
        if (sportsCount) {
            if (sportsCount <= this.tokensConfig.tokens.displayCategoriesMaxValues.sport) {
                result.sportsCatergory = token.filter.sports.map((sport) => sport.name).join(separator);
            } else {
                result.sportsCatergory = tokenCards.TokenCategorySelectedSports;
            }
            if (isLiveSport) {
                result.liveCategory = tokenCards.Live;
            }

            const competitionsCount = token.filter?.competitions?.length;

            if (competitionsCount) {
                if (competitionsCount <= this.tokensConfig.tokens.displayCategoriesMaxValues.competitions) {
                    result.competitionsCategory = token.filter.competitions.map((competition) => competition.name).join(separator);
                } else {
                    result.competitionsCategory = tokenCards.MultipleCompetitions;
                }
            }

            const meetingsCount = token.filter?.meetings?.length;

            if (meetingsCount) {
                if (meetingsCount <= this.tokensConfig.tokens.displayCategoriesMaxValues.competitions) {
                    result.meetingsCategory = token.filter.meetings.map((meeting) => meeting.name).join(separator);
                } else {
                    result.meetingsCategory = tokenCards.MultipleCompetitions;
                }
            }

            const fixturesCount = token.filter?.fixtures?.length;

            if (fixturesCount) {
                if (fixturesCount <= this.tokensConfig.tokens.displayCategoriesMaxValues.fixtures) {
                    result.fixturesCategory = token.filter.fixtures.map((fixture) => fixture.name).join(separator);
                } else {
                    result.fixturesCategory = tokenCards.TokenCategorySelectedEvents;
                }
            }

            const marketsCount = token.filter?.markets.length;

            if (marketsCount) {
                if (marketsCount <= this.tokensConfig.tokens.displayCategoriesMaxValues.markets) {
                    result.marketsCategory = token.filter.markets.map((market) => market.name).join(separator);
                } else {
                    result.marketsCategory = tokenCards.TokenCategorySelectedMarkets;
                }
            }

            const marketTemplatesCount = token.filter?.marketTemplates.length;

            if (marketTemplatesCount) {
                if (marketTemplatesCount <= this.tokensConfig.tokens.displayCategoriesMaxValues.templates) {
                    result.marketTemplatesCategory = token.filter.marketTemplates.map((marketTemplate) => marketTemplate.name).join(separator);
                } else {
                    result.marketTemplatesCategory = tokenCards.TokenCategorySelectedTemplates;
                }
            }

            const marketParametersCount = token.filter?.marketParameters.length;

            if (marketParametersCount) {
                if (marketParametersCount <= this.tokensConfig.tokens.displayCategoriesMaxValues.templates) {
                    const marketTypes = parseMarketType(token);
                    result.marketParametersCategory = marketTypes.join(separator);
                } else {
                    result.marketParametersCategory = tokenCards.TokenCategorySelectedParameters;
                }
            }

            const optionsCount = token.filter?.options.length;

            if (optionsCount) {
                if (optionsCount <= this.tokensConfig.tokens.displayCategoriesMaxValues.options) {
                    result.optionsCategory = token.filter.options.map((option) => option.name).join(separator);
                } else {
                    result.optionsCategory = tokenCards.TokenCategorySelectedSelections;
                }
            }
        } else {
            result.sportsCatergory = tokenCards.TokenCategoryAllSports;

            if (isLiveSport) {
                result.liveCategory = tokenCards.Live;
            }
        }

        return result;
    }

    getTokenExpires(expiryDate: Date): string {
        const now = DateProviderService.getDate();
        const today = DateProviderService.buildDate(now.getFullYear(), now.getMonth(), now.getDate() + 1);
        const tomorrow = DateProviderService.getDate(today.getTime() + this.DAY);
        const nowDate = DateProviderService.buildDate(now.getFullYear(), now.getMonth(), now.getDate());
        const expirDate = DateProviderService.buildDate(expiryDate.getFullYear(), expiryDate.getMonth(), expiryDate.getDate());
        const diffDay = expirDate.getTime() - nowDate.getTime();
        const diff = expiryDate.getTime() - now.getTime();
        const diffHours = diff / this.HOUR;
        if (this.isRewardDrawerEnabled) {
            return this.getRewardDrawerTokenExpires(diff, today, expiryDate, diffDay, tomorrow);
        }

        return this.getDefaultTokenExpires(diffHours, diffDay);
    }

    private getRewardDrawerTokenExpires(diff: number, today: Date, expiryDate: Date, diffDay: number, tomorrow: Date): string {
        if (diff < 0) {
            return this.sitecore.crmTokens.cards.TokenExpiredTemplate;
        }

        if (expiryDate < today) {
            return interpolateDoubleBracesString(this.sitecore.crmTokens.cards.TokenExpireTodayTemplate, {
                todayTime: this.intlService.formatDate(expiryDate, 'shortTime'),
            });
        }

        if (expiryDate < tomorrow) {
            return interpolateDoubleBracesString(this.sitecore.crmTokens.cards.TokenExpireTomorrowTemplate, {
                tomorrowTime: this.intlService.formatDate(expiryDate, 'shortTime'),
            });
        }

        if (diffDay <= this.DAY * 6) {
            return interpolateDoubleBracesString(this.sitecore.crmTokens.cards.TokenExpireDaysTemplate, {
                dayCount: diffDay / this.DAY,
            });
        }

        return this.intlService.formatDate(expiryDate, 'shortDate');
    }

    private getDefaultTokenExpires(diffHours: number, diffDay: number): string {
        if (diffHours < 0) {
            return this.sitecore.crmTokens.cards.TokenExpiredTemplate;
        }
        if (diffHours < 1) {
            return this.sitecore.crmTokens.cards.TokenExpireLessThanHourTemplate;
        }
        if (diffHours < 24) {
            return interpolateDoubleBracesString(this.sitecore.crmTokens.cards.TokenExpireHoursTemplate, {
                hoursCount: Math.floor(diffHours) + 1,
            });
        }

        return interpolateDoubleBracesString(this.sitecore.crmTokens.cards.TokenExpireDaysTemplate, {
            dayCount: diffDay / this.DAY,
        });
    }

    setDefaultValueForBannerExpanded(): void {
        this.isBannerExpanded = false;
        this._isBannerExpaned.next(this.isBannerExpanded);
    }

    setRecentlyClicked(userTokenId: string | null): void {
        if (this.isRewardDrawerEnabled) {
            this._recentlyClickedToken$.next(userTokenId);
        }
    }

    get isBannerExpanded$(): Observable<boolean> {
        return this._isBannerExpaned.asObservable();
    }

    toggleBannerExpansion(): void {
        this.isBannerExpanded = !this.isBannerExpanded;
        this._isBannerExpaned.next(this.isBannerExpanded);
    }

    private scrollOnBetNow(): void {
        const marqueeHeight = this._doc.querySelector('ms-marquee-widget:last-of-type')?.getBoundingClientRect().bottom ?? 0;
        const fixedHeaderHeight =
            this._doc.querySelector('ms-sitemap')?.scrollHeight ?? this._doc.querySelector('vn-header')?.getBoundingClientRect().bottom ?? 0;
        this.scrollContainer.scrollToDelta(0, marqueeHeight - fixedHeaderHeight);
    }
}
